import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { 
  LuNewspaper, 
  LuExternalLink, 
  LuCalendar, 
  LuTrendingUp,
  LuBell,
  LuArrowRight,
  LuRefreshCw,
  LuLinkedin,
  LuTwitter,
  LuRss,
  LuSparkles,
  LuMessageSquare,
  LuFileText,
  LuClipboardCheck,
  LuCreditCard,
  LuFolderOpen,
  LuCalendarDays
} from 'react-icons/lu';
import { useTheme, useSocket } from '../../context/SocketContext';

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// Notification type config
const getNotifConfig = (type) => {
  switch (type) {
    case "chat":
      return { icon: LuMessageSquare, borderColor: 'border-blue-200 dark:border-blue-800/50', bgColor: 'bg-blue-50 dark:bg-blue-900/20', iconBg: 'bg-blue-100 dark:bg-blue-900/50', iconColor: 'text-blue-600 dark:text-blue-400' };
    case "invoice":
      return { icon: LuFileText, borderColor: 'border-emerald-200 dark:border-emerald-800/50', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-900/50', iconColor: 'text-emerald-600 dark:text-emerald-400' };
    case "meeting":
      return { icon: LuCalendarDays, borderColor: 'border-purple-200 dark:border-purple-800/50', bgColor: 'bg-purple-50 dark:bg-purple-900/20', iconBg: 'bg-purple-100 dark:bg-purple-900/50', iconColor: 'text-purple-600 dark:text-purple-400' };
    case "checklist":
      return { icon: LuClipboardCheck, borderColor: 'border-amber-200 dark:border-amber-800/50', bgColor: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-900/50', iconColor: 'text-amber-600 dark:text-amber-400' };
    case "payment":
    case "service_purchase":
      return { icon: LuCreditCard, borderColor: 'border-pink-200 dark:border-pink-800/50', bgColor: 'bg-pink-50 dark:bg-pink-900/20', iconBg: 'bg-pink-100 dark:bg-pink-900/50', iconColor: 'text-pink-600 dark:text-pink-400' };
    case "document":
      return { icon: LuFolderOpen, borderColor: 'border-cyan-200 dark:border-cyan-800/50', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', iconBg: 'bg-cyan-100 dark:bg-cyan-900/50', iconColor: 'text-cyan-600 dark:text-cyan-400' };
    default:
      return { icon: LuBell, borderColor: 'border-gray-200 dark:border-dark-600', bgColor: 'bg-gray-50 dark:bg-dark-700', iconBg: 'bg-gray-200 dark:bg-dark-600', iconColor: 'text-gray-600 dark:text-gray-400' };
  }
};

const Dashboard = () => {
  const { currentThemeColors } = useTheme();
  const socket = useSocket();
  const navigate = useNavigate();
  const { setChatboxOpen } = useOutletContext();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);

  // Fetch recent blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/blogs?page=1&limit=4`
        );
        if (response.ok) {
          const data = await response.json();
          setBlogs(data.blogs || []);
        }
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUserData();
  }, []);

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    if (!user?._id) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/user/${user._id}`
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Socket.io real-time notifications
  useEffect(() => {
    if (!socket || !user?._id) return;

    socket.emit('joinRoom', user._id);

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    socket.on('newNotification', handleNewNotification);

    return () => {
      socket.off('newNotification', handleNewNotification);
    };
  }, [socket, user]);

  // Handle notification click on dashboard
  const handleNotifClick = async (notif) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${notif._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    if (notif.link === '/chat') {
      setChatboxOpen(true);
    } else if (notif.link) {
      navigate(notif.link);
    }
  };

  // Truncate text helper
  const truncateText = (html, maxLength) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const userDisplayName = (() => {
    if (!user) return 'there';
    if (user.firstName) {
      return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
    }
    if (user.fullName) return user.fullName;
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    return 'there';
  })();

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div 
        className="card p-6 md:p-8 opacity-0 animate-fade-in-up" 
        style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-2">
              Welcome back, <span style={{ color: 'var(--primary-500)' }}>{userDisplayName}</span>!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Stay updated with the latest news, blog posts, and notifications.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium">
              <LuCalendar className="inline-block w-4 h-4 mr-2" />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4 opacity-0 animate-fade-in-up" 
        style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
      >
        {[
          { label: 'Active Services', value: [user?.isTaxFilingEnabled, user?.isBookkeepingEnabled, user?.isUSAEnabled, user?.isCFOEnabled].filter(Boolean).length || 0, icon: LuTrendingUp, color: 'primary' },
          { label: 'New Blogs', value: blogs.length, icon: LuNewspaper, color: 'secondary' },
          { label: 'Notifications', value: notifications.filter(n => !n.isRead).length, icon: LuBell, color: 'accent' },
          { label: 'Reward Points', value: user?.referralCredits || 0, icon: LuSparkles, color: 'amber' }
        ].map((stat, index) => (
          <div 
            key={index}
            className="card p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            <div className="flex items-center gap-3">
              <div 
                className={`w-12 h-12 rounded-xl flex items-center justify-center`}
                style={{ 
                  background: stat.color === 'primary' ? 'var(--primary-100)' : 
                             stat.color === 'secondary' ? 'var(--secondary-100)' : 
                             stat.color === 'accent' ? 'var(--accent-100)' : 
                             'rgb(254, 243, 199)'
                }}
              >
                <stat.icon 
                  className="w-6 h-6"
                  style={{ 
                    color: stat.color === 'primary' ? 'var(--primary-600)' : 
                           stat.color === 'secondary' ? 'var(--secondary-600)' : 
                           stat.color === 'accent' ? 'var(--accent-600)' : 
                           'rgb(217, 119, 6)'
                  }}
                />
              </div>
              <div>
                <p className="text-2xl font-bold text-dark-800 dark:text-white">{stat.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Blog Posts - Takes 2 columns */}
        <div 
          className="lg:col-span-2 card p-6 opacity-0 animate-fade-in-up" 
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, var(--primary-500), var(--primary-600))' }}
              >
                <LuNewspaper className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-dark-800 dark:text-white">Latest Blog Posts</h2>
            </div>
            <a 
              href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              View All
              <LuArrowRight className="w-4 h-4" />
            </a>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-32 bg-gray-200 dark:bg-dark-600 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : blogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((blog, index) => (
                <a
                  key={blog._id}
                  href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog/${blog.slug || blog._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl overflow-hidden border border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  {blog.image && (
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${blog.image}`}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      {blog.category && (
                        <span className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                          {blog.category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-dark-800 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                      {truncateText(blog.description, 80)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                      <span className="flex items-center gap-1">
                        <LuCalendar className="w-3 h-3" />
                        {formatDate(blog.createdAt)}
                      </span>
                      <LuExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <LuNewspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No blog posts available</p>
            </div>
          )}
        </div>

        {/* Right Column - Real Notifications */}
        <div 
          className="card p-6 opacity-0 animate-fade-in-up" 
          style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--accent-500), var(--accent-600))' }}
            >
              <LuBell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-dark-800 dark:text-white">Notifications</h2>
              {notifications.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{notifications.filter(n => !n.isRead).length} unread</p>
              )}
            </div>
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {notifLoading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse p-4 rounded-xl border border-gray-200 dark:border-dark-600">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-dark-600" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-full mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))
            ) : notifications.length > 0 ? (
              notifications.slice(0, 10).map((notif) => {
                const config = getNotifConfig(notif.type);
                const IconComp = config.icon;
                return (
                  <div 
                    key={notif._id}
                    onClick={() => handleNotifClick(notif)}
                    className={`p-4 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-md cursor-pointer ${config.borderColor} ${config.bgColor}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.iconBg}`}>
                        <IconComp className={`w-4 h-4 ${config.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-dark-800 dark:text-white text-sm mb-1">
                          {notif.title}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                          {notif.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {timeAgo(notif.createdAt)}
                          </span>
                          {!notif.isRead && (
                            <span className="w-2 h-2 rounded-full bg-primary-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                  <LuBell className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">You'll see updates here when there's activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <div 
        className="card p-6 opacity-0 animate-fade-in-up" 
        style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: 'linear-gradient(135deg, var(--secondary-500), var(--secondary-600))' }}
          >
            <LuRss className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">Connect With Us</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LinkedIn Section */}
          <div className="rounded-xl border border-gray-200 dark:border-dark-600 overflow-hidden">
            <div className="p-4 bg-[#0077B5]/10 dark:bg-[#0077B5]/20 border-b border-gray-200 dark:border-dark-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0077B5] flex items-center justify-center">
                  <LuLinkedin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-800 dark:text-white">LinkedIn</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Follow us for updates</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Stay connected with BooksComply on LinkedIn for industry insights, company updates, and professional networking opportunities.
              </p>
              <a
                href="https://www.linkedin.com/company/bookscomply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0077B5] text-white text-sm font-medium hover:bg-[#006699] transition-colors"
              >
                <LuLinkedin className="w-4 h-4" />
                Follow on LinkedIn
                <LuExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Twitter/X Section */}
          <div className="rounded-xl border border-gray-200 dark:border-dark-600 overflow-hidden">
            <div className="p-4 bg-black/5 dark:bg-white/10 border-b border-gray-200 dark:border-dark-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black dark:bg-white flex items-center justify-center">
                  <LuTwitter className="w-5 h-5 text-white dark:text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-800 dark:text-white">Twitter / X</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Latest tweets & updates</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Follow @BooksComply on Twitter for quick tips, tax updates, and engaging content about financial compliance.
              </p>
              <a
                href="https://twitter.com/bookscomply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <LuTwitter className="w-4 h-4" />
                Follow on X
                <LuExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity / Updates Section */}
      <div 
        className="card p-6 opacity-0 animate-fade-in-up" 
        style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))' }}
            >
              <LuRefreshCw className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-dark-800 dark:text-white">What's New</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Feature Update 1 */}
          <div className="p-5 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <LuSparkles className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-lg mb-2">Enhanced Dashboard</h3>
            <p className="text-sm text-white/80 mb-3">
              New dashboard with real-time updates, blog feeds, and social media integration.
            </p>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">New</span>
          </div>

          {/* Feature Update 2 */}
          <div className="p-5 rounded-xl bg-gradient-to-br from-secondary-500 to-secondary-600 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <LuMessageSquare className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-lg mb-2">Live Chat Support</h3>
            <p className="text-sm text-white/80 mb-3">
              Get instant help from our support team through the chat feature.
            </p>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Available</span>
          </div>

          {/* Feature Update 3 */}
          <div className="p-5 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <LuTrendingUp className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-lg mb-2">Refer & Earn</h3>
            <p className="text-sm text-white/80 mb-3">
              Earn 1000 reward points for every successful referral you make.
            </p>
            <Link 
              to="/refer-and-earn"
              className="inline-block text-xs bg-white/20 px-2 py-1 rounded-full hover:bg-white/30 transition-colors"
            >
              Learn More →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
